import type { ReactNode } from "react";

import { Bar, BarChart, LabelList, XAxis } from "recharts";

import { Avatar, AvatarFallback } from "@/core/components/ui/avatar";
import { Button } from "@/core/components/ui/button";
import { Card, CardContent, CardHeader } from "@/core/components/ui/card";
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/core/components/ui/chart";
import { chartColor } from "@/core/components/ui/chart-colors";
import { NoTranslate } from "@/core/components/ui/no-translate";
import { Separator } from "@/core/components/ui/separator";
import { cn } from "@/core/utils/func/cn";

import { CardOptionsMenu } from "../card-options-menu";

export type TotalTransactionChartDatum = {
  month: string;
  transaction: number;
};

export type TotalTransactionStat = {
  icon: ReactNode;
  label: string;
  value: string;
  iconClassName?: string;
};

export type TotalTransactionCardProps = {
  title: string;
  subTitle: string;
  reportTitle: string;
  reportSubTitle: string;
  chartData: TotalTransactionChartDatum[];
  stats: TotalTransactionStat[];
  performanceLabel: string;
  performanceValue: string;
  transactionLabel?: string;
  actionLabel?: string;
  onAction?: () => void;
  menuItems?: string[];
  menuAriaLabel?: string;
  onMenuSelect?: (item: string) => void;
  className?: string;
};

export const TotalTransactionCard = ({
  title,
  subTitle,
  reportTitle,
  reportSubTitle,
  chartData,
  stats,
  performanceLabel,
  performanceValue,
  transactionLabel,
  actionLabel,
  onAction,
  menuItems,
  menuAriaLabel,
  onMenuSelect,
  className,
}: TotalTransactionCardProps) => {
  const chartConfig = {
    transaction: {
      label: transactionLabel,
    },
  } satisfies ChartConfig;

  const data = chartData.map((item, index) => ({
    ...item,
    fill:
      index % 2 === 1 ? chartColor("accent") : chartColor("accent", 0.2),
  }));

  return (
    <Card className={cn("relative grid grid-cols-1 gap-4 md:grid-cols-5", className)}>
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-1 rounded-t-[20px] bg-gradient-accent"
      />
      <div className="max-md:border-b md:col-span-3 md:border-r md:pr-4">
        <CardHeader className="flex flex-row items-start justify-between">
          <div className="flex flex-col gap-1">
            <span className="text-lg font-semibold">{title}</span>
            <span className="text-sm text-muted-foreground">{subTitle}</span>
          </div>
          <CardOptionsMenu
            items={menuItems}
            ariaLabel={menuAriaLabel}
            onSelect={onMenuSelect}
          />
        </CardHeader>
        <CardContent className="max-md:pb-6">
          <ChartContainer config={chartConfig} className="h-[332px] w-full">
            <BarChart
              accessibilityLayer
              data={data}
              barSize={35}
              margin={{
                top: 7,
              }}
            >
              <XAxis
                dataKey="month"
                tickLine={false}
                tickMargin={5.5}
                axisLine={false}
                tickFormatter={(value) => String(value).slice(0, 3)}
                tick={{ fontSize: 14, fill: "hsl(var(--muted-foreground))" }}
              />
              <ChartTooltip
                cursor={false}
                content={<ChartTooltipContent hideLabel />}
              />
              <Bar dataKey="transaction" radius={10}>
                <LabelList
                  position="top"
                  offset={12}
                  className="notranslate fill-card-foreground font-semibold"
                  fontSize={16}
                  formatter={(value: unknown) =>
                    `${String(value).slice(0, 2)}K`
                  }
                />
              </Bar>
            </BarChart>
          </ChartContainer>
        </CardContent>
      </div>
      <div className="flex flex-col gap-8 md:col-span-2">
        <CardHeader className="flex flex-row items-start justify-between">
          <div className="flex flex-col gap-1">
            <span className="text-lg font-semibold">{reportTitle}</span>
            <NoTranslate as="span" className="text-sm text-muted-foreground">
              {reportSubTitle}
            </NoTranslate>
          </div>
          <CardOptionsMenu
            items={menuItems}
            ariaLabel={menuAriaLabel}
            onSelect={onMenuSelect}
          />
        </CardHeader>
        <CardContent className="flex flex-1 items-center text-base">
          <div className="flex flex-1 justify-around gap-1">
            {stats.map((stat, index) => (
              <div key={index} className="contents">
                {index > 0 ? (
                  <Separator orientation="vertical" className="!h-[inherit]" />
                ) : null}
                <div className="flex flex-col items-center gap-4 p-2">
                  <Avatar className="size-12 rounded-sm">
                    <AvatarFallback
                      className={cn(
                        "shrink-0 rounded-sm bg-accent/10 text-accent-dark dark:text-accent-light",
                        stat.iconClassName
                      )}
                    >
                      {stat.icon}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col items-center gap-1">
                    <span className="text-muted-foreground">{stat.label}</span>
                    <NoTranslate as="span" className="text-2xl font-medium">
                      {stat.value}
                    </NoTranslate>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
        <div className="px-6">
          <Separator />
        </div>
        <div className="flex items-center justify-between gap-2 px-6">
          <div className="flex flex-col gap-2">
            <span className="text-base text-muted-foreground">
              {performanceLabel}
            </span>
            <NoTranslate as="span" className="text-xl font-medium">
              {performanceValue}
            </NoTranslate>
          </div>
          {actionLabel ? (
            <Button onClick={onAction}>{actionLabel}</Button>
          ) : null}
        </div>
      </div>
    </Card>
  );
};
